<template>
  <Combobox as="div" v-model="selected" nullable>
    <div class="relative w-full">
      <ComboboxInput
        class="relative flex w-full items-center justify-between border border-grey-30 bg-white px-4 py-2 text-left text-sm text-black"
        autocomplete="off"
        placeholder="Enter a Postcode or Location"
        :display-value="(selected) => selected?.location"
        @change="query = $event.target.value"
      />
      <ComboboxOptions v-if="shouldShowItems"
        class="shadow-lg absolute z-10 max-h-60 w-full overflow-y-scroll border border-grey-30 bg-white py-1"
      >
        <li
          v-if="filtering"
          class="relative cursor-default select-none py-2 pl-10 pr-4"
        >
          Retrieving your locations
        </li>
        <li
          v-if="!filtering && filteredLocations.length === 0 && tooShort"
          class="relative cursor-default select-none py-2 pl-10 pr-4"
        >
          (Minimum {{ minChars }} characters needed)
        </li>
        <li
          v-if="!filtering && filteredLocations.length === 0 && !tooShort"
          class="relative cursor-default select-none py-2 pl-10 pr-4"
        >
          No locations found
        </li>

        <ComboboxOption
          v-if="!filtering"
          v-for="item in filteredLocations"
          :key="item.id"
          :value="item"
          v-slot="{ active }"
        >
          <li
            class="relative cursor-pointer select-none py-2 pl-10 pr-4"
            :class="{
              'bg-grey-20': active,
            }"
          >
            {{ item.location }}
            <template v-if="item?.locationSubHeading">
              ({{ item.locationSubHeading }})
            </template>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>
  </Combobox>
  <input-error-popup
      :error-message="errorMessage"
      v-if="this.showErrorPopup"
  />
</template>

<script>
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';
import { debounce, isInteger } from 'lodash';
import InputErrorPopup from "Components/InputErrorPopup.vue";
import showErrorPopup from 'Mixins/showErrorPopup';
import input from 'Mixins/input';

export default {
  name: 'InputLocation',
  components: {
    InputErrorPopup,
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
  },
  mixins: [input, showErrorPopup],
  props: {
    value: {
      type: Object,
      default: '',
    },
    locations: {
      type: Array,
      default: [],
    },
    minChars: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      query: '',
      previousQuery: null,
      filteredLocations: [],
      filtering: false,
      tooShort: false,
      shouldShowItems: false,
      debouncedFilterItems: debounce(this.filterItems, 300),
    };
  },
  methods: {
    reformatQueryIfPostcode(q) {
        // fudge some stuff for postcodes here, V2 cheated
        // original version from V2 - did not support partial postcodes, only matched full
        // let postcodeRegex = /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/;

        // updated one (with a little help from an AI as I suck at regex's)
        let postcodeRegex = /^(GIR 0AA|[A-Za-z]{1,2}[0-9][A-HJ-Y0-9]?\s?[0-9]?[A-Za-z]{0,2})$/i;

        var nonAlphaNumeric = /[^A-Za-z0-9 ]+/g;
        let score = 0;
        let isPostcode = false;

        let tempQ = q.replace(/^\s+|\s+$|\s+(?=\s)/g, "");
        if (tempQ.match(postcodeRegex)) {
            let match = tempQ.match(/^([A-Za-z]{1,2}[0-9][A-HJ-Y0-9]?)\s?[0-9]?[A-Za-z]{0,2}$/i);
            if (match) {
                score = 1;
                isPostcode = true;
                tempQ = match[1];
            }            
        }

        // quote regex
        if (isPostcode) {
            // In case of there being a space in the entered postcode
            q = tempQ.trim();
            q = q.replace(new RegExp('[.\\\\+*?\\[\\^\\]$(){}=!<>|:\\' + ('/' || '') + '-]', 'g'), '\\$&');
            // query = "^" + query;
        } else {
            q = q.replace(nonAlphaNumeric, '');
        }

        q = q.toLowerCase().trim();
        //console.log(`isPostcode: ${isPostcode}, tempQ: ${tempQ}, look for: ${q}`);
        return q;
    },
    async filterItems() {
        // this.filtering = true;

        //await this.$nextTick();

        let toSearchFor = this.reformatQueryIfPostcode(this.query);

        this.filteredLocations =
            this.query == '' || this.query.length < this.minChars
            ? []
            : // If the current query is an extension of the previous query, only search the filtered results
                (this.previousQuery && this.query.startsWith(this.previousQuery) 
                ? this.filteredLocations
                : this.locations
                ).filter((location) =>
                location?.location
                    .toLowerCase()
                    .includes(toSearchFor)
                );
        this.previousQuery = this.query;

        // if (this.filteredLocations.length == 0) {
        //     this.tooShort = true;
        // }

        //await this.$nextTick();

        this.filtering = false;
    },
  },
  watch: {
    async query() {

        this.shouldShowItems =
            this.query != '' && this.query.length > 0
            ? true
            : false;

        if (!this.shouldShowItems || this.query.length < this.minChars) {
            this.filteredLocations = [];
            this.previousQuery = '';
        }

        this.tooShort =
            this.query != '' && this.query.length < this.minChars && this.filteredLocations.length === 0
            ? true
            : false;

        if (this.query.length > (this.minChars - 1) && this.query !== '') {
            this.filtering = true;
            this.debouncedFilterItems();
        }
    },
  },
  computed: {
    selected: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('changeLocation', value);
      },
    },
    buttonClassnames() {
      return {
        'bg-red-200': this.error,
      };
    },
  },
};
</script>
